import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Lock, Eye, EyeOff, ArrowRight, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { setSession } = useAuth();
  const token = params.get("token");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setErr("Enlace inválido. Solicita uno nuevo.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== pwd2) {
      setErr("Las contraseñas no coinciden");
      return;
    }
    if (pwd.length < 6) {
      setErr("Mínimo 6 caracteres");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const r = await api.post("/auth/reset-password", { token, new_password: pwd });
      if (setSession) {
        setSession(r.data.token, r.data.user);
      } else {
        // Fallback: persist manually
        localStorage.setItem("td_token", r.data.token);
        localStorage.setItem("td_user", JSON.stringify(r.data.user));
      }
      setDone(true);
      setTimeout(() => nav("/", { replace: true }), 1500);
    } catch (e) {
      setErr(e?.response?.data?.detail || "No se pudo restablecer la contraseña");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#0b0a10] text-white overflow-hidden flex flex-col">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-[-15%] left-[-10%] w-[700px] h-[700px] rounded-full bg-violet-600/20 blur-[140px]" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full bg-fuchsia-500/15 blur-[140px]" />
      </div>

      <header className="px-6 sm:px-10 py-5 flex items-center">
        <Link to="/" className="flex items-center gap-3" data-testid="reset-brand">
          <img src="/logo.png" alt="The Trading Dock" className="w-11 h-11 rounded-2xl ring-1 ring-white/15" />
          <div className="leading-tight">
            <div className="text-base sm:text-lg font-black tracking-tight">THE TRADING DOCK<sup className="text-[9px] opacity-60 ml-0.5">®</sup></div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">finance</div>
          </div>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 sm:px-8 py-8">
        <div className="w-full max-w-md rounded-3xl bg-[#131218]/70 backdrop-blur-2xl border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] p-7 sm:p-9">
          {done ? (
            <div className="text-center" data-testid="reset-success">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-7 h-7 text-emerald-300" />
              </div>
              <h1 className="mt-5 text-2xl sm:text-3xl font-black tracking-tight">Contraseña actualizada</h1>
              <p className="mt-3 text-sm text-slate-400">Redirigiendo al panel...</p>
            </div>
          ) : (
            <>
              <div className="text-[10px] uppercase tracking-[0.28em] text-violet-300/80 font-bold mb-2">NUEVA CONTRASEÑA</div>
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight leading-[1.05]" data-testid="reset-title">
                Crea una<br /><span className="brand-mark">contraseña fuerte.</span>
              </h1>

              <form onSubmit={submit} className="mt-7 space-y-4" data-testid="reset-form">
                <Field icon={Lock} label="Nueva contraseña">
                  <input
                    data-testid="reset-pwd-input"
                    type={show ? "text" : "password"}
                    value={pwd} onChange={(e) => setPwd(e.target.value)}
                    required minLength={6} placeholder="••••••••"
                    className="w-full bg-transparent outline-none text-white placeholder:text-slate-600 text-sm"
                  />
                  <button type="button" onClick={() => setShow(!show)} className="text-slate-500 hover:text-white">
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </Field>
                <Field icon={Lock} label="Confirmar nueva contraseña">
                  <input
                    data-testid="reset-pwd2-input"
                    type={show ? "text" : "password"}
                    value={pwd2} onChange={(e) => setPwd2(e.target.value)}
                    required minLength={6} placeholder="••••••••"
                    className="w-full bg-transparent outline-none text-white placeholder:text-slate-600 text-sm"
                  />
                </Field>

                {err && (
                  <div data-testid="reset-error" className="flex items-start gap-2 text-sm text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{err}</span>
                  </div>
                )}

                <button
                  type="submit" disabled={loading || !token}
                  data-testid="reset-submit"
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-400 hover:to-fuchsia-400 text-white font-semibold py-3.5 rounded-xl transition-all shadow-[0_10px_40px_-12px_rgba(139,92,246,0.7)] disabled:opacity-60"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Actualizar y entrar <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>

              <Link to="/forgot-password" className="block mt-5 text-center text-xs text-slate-500 hover:text-slate-300" data-testid="reset-request-new">
                ¿El enlace expiró? Solicita uno nuevo
              </Link>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1.5">{label}</div>
      <div className="flex items-center gap-3 bg-white/[0.04] border border-white/10 focus-within:border-violet-500/60 focus-within:bg-white/[0.06] rounded-xl px-4 py-3 transition-all">
        <Icon className="w-4 h-4 text-slate-500" />
        {children}
      </div>
    </label>
  );
}
